import { QueryResult } from '@apollo/client'
import { useCallback, useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'

import { GetTenantStoresQuery } from '@/graphql'
import useAuth from '@/hooks/useAuth'
import useLoggedInUser from '@/hooks/useLoggedInUser'

import { AccountSetUpHelpers } from '../../utils/account-set-up.helpers'
import { AccountSetUpStep } from './types'

interface UseAccountSetUpNavigationProps {
  flow: AccountSetUpStep[]
  loading: boolean
  loggedInUser: ReturnType<typeof useLoggedInUser>
  auth: ReturnType<typeof useAuth>
  tenantStores: QueryResult<GetTenantStoresQuery>
  skipPayment: boolean
  paymentSuccess: boolean
}

export const useAccountSetUpNavigation = ({
  flow,
  loading,
  auth,
  loggedInUser,
  tenantStores,
  skipPayment,
  paymentSuccess,
}: UseAccountSetUpNavigationProps) => {
  const navigate = useNavigate()
  const [isValid, setIsValid] = useState(false)
  const [searchParams, setSearchParams] = useSearchParams()
  const currentStep = searchParams.get('step')

  const handleGoToHome = () => {
    navigate('/', { replace: true })
  }

  useEffect(() => {
    if (loading || !flow.length) {
      return // not ready to validate step
    }

    const isCompleteProps = {
      authUser: auth.user,
      user: loggedInUser.user,
      skipPayment,
      paymentSuccess,
      tenantStores: tenantStores.data?.tenantStores.results || [],
    }

    const firstIncomplete = AccountSetUpHelpers.getFirstIncompleteStep({
      flow,
      isCompleteProps,
    })

    if (
      AccountSetUpHelpers.isValidStep({
        stepKey: currentStep,
        firstIncomplete,
        flow,
        isCompleteProps,
      })
    ) {
      setIsValid(true)
    } else {
      setIsValid(false)

      // navigate away from invalid step
      if (firstIncomplete) {
        // go to the first incomplete step
        navigate(`/account-set-up?step=${firstIncomplete.key}`, {
          replace: true,
        })
      } else if (currentStep) {
        // try the next step
        nextStep()
      } else {
        // all steps are complete
        handleGoToHome()
      }
    }
  }, [
    loading,
    flow,
    currentStep,
    auth.user,
    loggedInUser.user,
    skipPayment,
    paymentSuccess,
    tenantStores.data?.tenantStores.results,
  ])

  const nextStep = useCallback(() => {
    if (loading) {
      return
    }

    const currentIndex = AccountSetUpHelpers.getCurrentStepIndex({
      currentStep,
      flow,
    })

    if (currentIndex === flow.length - 1) {
      // all steps are complete
      handleGoToHome()
    } else {
      const nextStep = flow[currentIndex + 1]
      const newParams = new URLSearchParams(searchParams)
      if (nextStep) {
        newParams.set('step', nextStep.key)
        setSearchParams(newParams)
      }
    }
  }, [loading, currentStep, flow, searchParams])

  return {
    isValid,
    currentStep,
    nextStep,
  }
}
